import React, { useState, useEffect } from 'react';
import { CodelcoLayout } from '../../components/codelco/CodelcoLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { Save, Plug, Cloud, RefreshCw, Eye, EyeOff, AlertCircle, CheckCircle2, Bell, Mail, Plus, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CodelcoConfigPage() {
    const [cfg, setCfg] = useState({
        foursat_email: '',
        foursat_password: '',
        foursat_api_hash: '',
        samtech_url: 'http://wspos.samtech.cl/WSP.asmx',
        samtech_login: '',
        samtech_password: '',
        tercero_default: 'Codelco DCH',
        utc_offset: 0,
        frecuencia_movimiento_seg: 60,
        frecuencia_detenido_seg: 3600,
        frecuencia_polling_seg: 60,
        modo_prueba: true,
        scheduler_activo: false,
        notif_umbral_errores: 3,
        notif_push_activo: true,
        notif_email_activo: false,
        notif_emails: [],
        brevo_api_key: '',
    });
    const [newEmail, setNewEmail] = useState('');
    const [showFsPw, setShowFsPw] = useState(false);
    const [showSamPw, setShowSamPw] = useState(false);
    const [loading, setLoading] = useState(true);
    const [testing, setTesting] = useState({ four: false, sam: false });
    const [testResult, setTestResult] = useState({ four: null, sam: null });

    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    useEffect(() => { loadConfig(); }, []);

    const safeJson = async (r) => {
        const txt = await r.text();
        try { return txt ? JSON.parse(txt) : {}; } catch { return { detail: txt || `HTTP ${r.status}` }; }
    };

    const loadConfig = async () => {
        try {
            const r = await fetch(`${API_URL}/api/codelco/config`, { headers });
            if (r.ok) {
                const d = await safeJson(r);
                setCfg(prev => ({ ...prev, ...d, foursat_password: '', samtech_password: '' }));
            }
        } catch { /* ignore */ }
        setLoading(false);
    };

    const save = async () => {
        try {
            const r = await fetch(`${API_URL}/api/codelco/config`, {
                method: 'PUT', headers,
                body: JSON.stringify(cfg),
            });
            const d = await safeJson(r);
            if (r.ok) {
                toast.success('Configuración guardada');
                setCfg(prev => ({ ...prev, foursat_password: '', samtech_password: '' }));
                loadConfig();
            } else {
                toast.error(d.detail || 'Error');
            }
        } catch (e) { toast.error('Error de conexión: ' + String(e).substring(0, 120)); }
    };

    const testFoursat = async () => {
        setTesting(t => ({ ...t, four: true }));
        setTestResult(r => ({ ...r, four: null }));
        try {
            const r = await fetch(`${API_URL}/api/codelco/test-4sat`, { method: 'POST', headers });
            const d = await safeJson(r);
            if (r.ok) setTestResult(s => ({ ...s, four: { ok: true, msg: `${d.dispositivos_encontrados || 0} dispositivos encontrados` } }));
            else setTestResult(s => ({ ...s, four: { ok: false, msg: d.detail || `HTTP ${r.status}` } }));
        } catch (e) { setTestResult(s => ({ ...s, four: { ok: false, msg: String(e).substring(0, 200) } })); }
        setTesting(t => ({ ...t, four: false }));
    };

    const testSamtech = async () => {
        setTesting(t => ({ ...t, sam: true }));
        setTestResult(r => ({ ...r, sam: null }));
        try {
            const r = await fetch(`${API_URL}/api/codelco/test-samtech`, { method: 'POST', headers });
            const d = await safeJson(r);
            setTestResult(s => ({ ...s, sam: { ok: !!d.ok, msg: d.body || d.detail || `HTTP ${r.status}`, raw: d.raw || '', status: d.status_code } }));
        } catch (e) { setTestResult(s => ({ ...s, sam: { ok: false, msg: String(e).substring(0, 200), raw: '' } })); }
        setTesting(t => ({ ...t, sam: false }));
    };

    if (loading) return <CodelcoLayout><div className="p-8">Cargando...</div></CodelcoLayout>;

    return (
        <CodelcoLayout>
            <div className="max-w-4xl mx-auto space-y-6 pb-12">
                <div>
                    <h1 className="text-3xl font-black tracking-tight">Configuración</h1>
                    <p className="text-muted-foreground">Credenciales y parámetros del integrador</p>
                </div>

                {/* Master switches */}
                <Card className="border-2">
                    <CardHeader>
                        <CardTitle>Estado del integrador</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <Label className="text-base">Scheduler activo</Label>
                                <p className="text-xs text-muted-foreground">Envío automático según frecuencias configuradas</p>
                            </div>
                            <Switch checked={cfg.scheduler_activo} onCheckedChange={v => setCfg({ ...cfg, scheduler_activo: v })} data-testid="switch-scheduler" />
                        </div>
                        <Separator />
                        <div className="flex items-center justify-between">
                            <div>
                                <Label className="text-base">Modo prueba (mock)</Label>
                                <p className="text-xs text-muted-foreground">Si está ON, no envía al WS real; simula respuestas OK</p>
                            </div>
                            <Switch checked={cfg.modo_prueba} onCheckedChange={v => setCfg({ ...cfg, modo_prueba: v })} data-testid="switch-mock" />
                        </div>
                    </CardContent>
                </Card>

                {/* 4sat */}
                <Card className="border-2">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2"><Cloud className="h-5 w-5 text-blue-500" /> Conexión 4sat</CardTitle>
                        <CardDescription>Origen de los datos GPS</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Email 4sat</Label>
                                <Input value={cfg.foursat_email} onChange={e => setCfg({ ...cfg, foursat_email: e.target.value })} placeholder="codelco@4sat.cl" data-testid="input-4sat-email" />
                            </div>
                            <div className="space-y-2">
                                <Label>Contraseña 4sat <span className="text-xs text-muted-foreground">(opcional si tienes hash)</span></Label>
                                <div className="flex gap-2">
                                    <Input type={showFsPw ? 'text' : 'password'} value={cfg.foursat_password} onChange={e => setCfg({ ...cfg, foursat_password: e.target.value })} placeholder={cfg.foursat_password_set ? '••••••••' : 'Dejar vacío si no cambia'} data-testid="input-4sat-pass" />
                                    <Button variant="outline" size="icon" onClick={() => setShowFsPw(!showFsPw)} type="button">
                                        {showFsPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </Button>
                                </div>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>user_api_hash <span className="text-xs text-red-500">*requerido</span></Label>
                            <Input value={cfg.foursat_api_hash} onChange={e => setCfg({ ...cfg, foursat_api_hash: e.target.value })} placeholder="$2y$10$..." className="font-mono text-xs" data-testid="input-4sat-hash" />
                            <p className="text-xs text-muted-foreground">Obtenido del endpoint de login de 4sat o entregado por el equipo de soporte.</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button onClick={testFoursat} disabled={testing.four} variant="outline" data-testid="btn-test-4sat">
                                {testing.four ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Plug className="h-4 w-4 mr-2" />}
                                Probar conexión 4sat
                            </Button>
                            {testResult.four && (
                                <div className={`flex items-center gap-2 text-sm ${testResult.four.ok ? 'text-emerald-600' : 'text-red-500'}`}>
                                    {testResult.four.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                                    <span className="truncate max-w-md">{testResult.four.msg}</span>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Samtech */}
                <Card className="border-2">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2"><Cloud className="h-5 w-5 text-orange-500" /> Conexión Samtech (Codelco DCH)</CardTitle>
                        <CardDescription>Destino de los envíos SOAP</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label>URL WS</Label>
                            <Input value={cfg.samtech_url} onChange={e => setCfg({ ...cfg, samtech_url: e.target.value })} className="font-mono text-xs" data-testid="input-samtech-url" />
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Login Samtech</Label>
                                <Input value={cfg.samtech_login} onChange={e => setCfg({ ...cfg, samtech_login: e.target.value })} placeholder="proveedor_codelco" data-testid="input-samtech-login" />
                            </div>
                            <div className="space-y-2">
                                <Label>Password Samtech</Label>
                                <div className="flex gap-2">
                                    <Input type={showSamPw ? 'text' : 'password'} value={cfg.samtech_password} onChange={e => setCfg({ ...cfg, samtech_password: e.target.value })} placeholder={cfg.samtech_password_set ? '••••••••' : 'Solicitar a cpalacios@samtech.cl'} data-testid="input-samtech-pass" />
                                    <Button variant="outline" size="icon" onClick={() => setShowSamPw(!showSamPw)} type="button">
                                        {showSamPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                    </Button>
                                </div>
                            </div>
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Tercero (cliente) default</Label>
                                <Input value={cfg.tercero_default} onChange={e => setCfg({ ...cfg, tercero_default: e.target.value })} data-testid="input-tercero" />
                                <p className="text-xs text-muted-foreground">Aparece en &lt;tercero&gt; del XML. Editable por vehículo.</p>
                            </div>
                            <div className="space-y-2">
                                <Label>UTC Offset (horas)</Label>
                                <Input type="number" step="0.5" min="-12" max="14" value={cfg.utc_offset} onChange={e => setCfg({ ...cfg, utc_offset: parseFloat(e.target.value) || 0 })} data-testid="input-utc-offset" />
                                <p className="text-xs text-muted-foreground">
                                    Offset aplicado al campo &lt;fn&gt; del XML. Default 0 (UTC). Chile: -3 (-4 en invierno). Argentina: -3. Perú: -5.
                                </p>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Button onClick={testSamtech} disabled={testing.sam} variant="outline" data-testid="btn-test-samtech">
                                {testing.sam ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Plug className="h-4 w-4 mr-2" />}
                                Probar envío SOAP
                            </Button>
                            {testResult.sam && (
                                <div className={`p-3 rounded-lg border-2 ${testResult.sam.ok ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-red-500/30 bg-red-500/5'}`}>
                                    <div className={`flex items-center gap-2 text-sm font-bold ${testResult.sam.ok ? 'text-emerald-600' : 'text-red-500'}`}>
                                        {testResult.sam.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                                        {testResult.sam.ok ? 'Envío exitoso' : 'Error en envío'}
                                        {testResult.sam.status ? <span className="text-xs font-normal text-muted-foreground">HTTP {testResult.sam.status}</span> : null}
                                    </div>
                                    <p className="text-xs mt-2 font-mono whitespace-pre-wrap break-all">{testResult.sam.msg}</p>
                                    {testResult.sam.raw && (
                                        <details className="mt-2">
                                            <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">Ver respuesta XML completa</summary>
                                            <pre className="text-[10px] bg-muted p-2 rounded mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-all">{testResult.sam.raw}</pre>
                                        </details>
                                    )}
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-2">
                    <CardHeader>
                        <CardTitle>Frecuencia de envío</CardTitle>
                        <CardDescription>Tiempo mínimo entre envíos por vehículo. El manual sugiere 60s en movimiento, 3600s detenido (1h). Mínimo aceptado: 10s.</CardDescription>
                    </CardHeader>
                    <CardContent className="grid md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                            <Label>En movimiento (segundos)</Label>
                            <Input type="number" min="10" max="3600" value={cfg.frecuencia_movimiento_seg} onChange={e => setCfg({ ...cfg, frecuencia_movimiento_seg: parseInt(e.target.value) || 60 })} data-testid="input-freq-mov" />
                            <p className="text-xs text-muted-foreground">
                                = {Math.floor(cfg.frecuencia_movimiento_seg / 60) > 0 ? `${Math.floor(cfg.frecuencia_movimiento_seg / 60)}m ` : ''}{cfg.frecuencia_movimiento_seg % 60}s
                            </p>
                        </div>
                        <div className="space-y-2">
                            <Label>Detenido (segundos)</Label>
                            <Input type="number" min="10" max="86400" value={cfg.frecuencia_detenido_seg} onChange={e => setCfg({ ...cfg, frecuencia_detenido_seg: parseInt(e.target.value) || 3600 })} data-testid="input-freq-det" />
                            <p className="text-xs text-muted-foreground">
                                = {Math.floor(cfg.frecuencia_detenido_seg / 3600) > 0 ? `${Math.floor(cfg.frecuencia_detenido_seg / 3600)}h ` : ''}{Math.floor((cfg.frecuencia_detenido_seg % 3600) / 60)}m {cfg.frecuencia_detenido_seg % 60}s
                            </p>
                        </div>
                        <div className="space-y-2">
                            <Label>Polling de estado (segundos)</Label>
                            <Input type="number" min="10" max="600" value={cfg.frecuencia_polling_seg || 60} onChange={e => setCfg({ ...cfg, frecuencia_polling_seg: parseInt(e.target.value) || 60 })} data-testid="input-freq-polling" />
                            <p className="text-xs text-muted-foreground">
                                Cada cuánto el scheduler consulta 4sat para detectar cambios de ignición (prendido/apagado). No genera envío a Samtech, solo lectura. Recomendado: 60s.
                            </p>
                        </div>
                    </CardContent>
                </Card>

                {/* Notificaciones */}
                <Card className="border-2">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2"><Bell className="h-5 w-5 text-amber-500" /> Alertas por errores consecutivos</CardTitle>
                        <CardDescription>Envía aviso al admin si un vehículo acumula varios envíos fallidos</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label>Umbral de errores consecutivos para alertar</Label>
                            <Input type="number" min="1" max="20" value={cfg.notif_umbral_errores} onChange={e => setCfg({ ...cfg, notif_umbral_errores: parseInt(e.target.value) || 3 })} data-testid="input-umbral-errores" />
                            <p className="text-xs text-muted-foreground">Al llegar a este número de errores seguidos, se crea una notificación y se envían los canales activos. Hay un anti-spam de 6h por vehículo.</p>
                        </div>
                        <div className="flex items-center justify-between gap-3 pt-2 border-t">
                            <div>
                                <Label className="flex items-center gap-2"><Bell className="h-4 w-4" /> Push (al admin)</Label>
                                <p className="text-xs text-muted-foreground">Usa las suscripciones del módulo Alarmas si están activas</p>
                            </div>
                            <Switch checked={cfg.notif_push_activo} onCheckedChange={v => setCfg({ ...cfg, notif_push_activo: v })} data-testid="switch-notif-push" />
                        </div>
                        <div className="flex items-center justify-between gap-3 pt-2 border-t">
                            <div>
                                <Label className="flex items-center gap-2"><Mail className="h-4 w-4" /> Email (vía Brevo)</Label>
                                <p className="text-xs text-muted-foreground">Requiere API Key de Brevo y al menos un destinatario</p>
                            </div>
                            <Switch checked={cfg.notif_email_activo} onCheckedChange={v => setCfg({ ...cfg, notif_email_activo: v })} data-testid="switch-notif-email" />
                        </div>
                        {cfg.notif_email_activo && (
                            <div className="space-y-3 pl-2 border-l-2 border-amber-500/40">
                                <div className="space-y-2">
                                    <Label>Brevo API Key</Label>
                                    <Input type="password" value={cfg.brevo_api_key || ''} onChange={e => setCfg({ ...cfg, brevo_api_key: e.target.value })} placeholder="xkeysib-..." data-testid="input-brevo-key" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Emails destinatarios</Label>
                                    <div className="flex gap-2">
                                        <Input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="admin@empresa.cl" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (newEmail.includes('@')) { setCfg({ ...cfg, notif_emails: [...(cfg.notif_emails || []), newEmail.trim()] }); setNewEmail(''); } } }} data-testid="input-new-email" />
                                        <Button type="button" variant="outline" onClick={() => { if (newEmail.includes('@')) { setCfg({ ...cfg, notif_emails: [...(cfg.notif_emails || []), newEmail.trim()] }); setNewEmail(''); } }}>
                                            <Plus className="h-4 w-4" />
                                        </Button>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {(cfg.notif_emails || []).map((em, i) => (
                                            <span key={i} className="inline-flex items-center gap-1 bg-muted px-2 py-1 rounded-full text-xs">
                                                {em}
                                                <button onClick={() => setCfg({ ...cfg, notif_emails: cfg.notif_emails.filter((_, j) => j !== i) })} className="hover:text-red-500">
                                                    <X className="h-3 w-3" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <div className="sticky bottom-0 bg-background pt-4 pb-2 border-t">
                    <Button onClick={save} size="lg" className="w-full bg-gradient-to-r from-amber-500 to-orange-600 text-white" data-testid="btn-save-config">
                        <Save className="h-5 w-5 mr-2" /> Guardar configuración
                    </Button>
                </div>
            </div>
        </CodelcoLayout>
    );
}
